import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import {
  slackService,
  SlackConnectionResponse,
  UpdateSlackConnectionDto,
} from '../services/slackService';

export const slackConnectionKeys = {
  all: ['slackConnection'] as const,
  detail: () => [...slackConnectionKeys.all, 'detail'] as const,
};

interface SyncDetail {
  email: string;
  slackUserId: string;
  displayName: string;
  status: 'updated' | 'not_found' | 'no_change';
}

interface SyncResult {
  synced: number;
  updated: number;
  notFound: number;
  details: SyncDetail[];
}

interface TestBirthdayDto {
  userName: string;
  userId: string;
  userPhotoUrl?: string;
}

interface TestAnniversaryDto {
  userName: string;
  userId: string;
  years: number;
  userPhotoUrl?: string;
}

/**
 * Hook: useSlackConnection
 * Fetch the organization's Slack connection and notification channels.
 */
export const useSlackConnection = (enabled = true) => {
  return useQuery<SlackConnectionResponse>({
    queryKey: slackConnectionKeys.detail(),
    queryFn: () => slackService.getConnection(),
    enabled,
    staleTime: 0,
    refetchOnMount: true,
  });
};

/**
 * Hook: useUpdateSlackConnection
 * Update the Slack connection (admin/manager only) and refresh the cache.
 */
export const useUpdateSlackConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSlackConnectionDto) => slackService.updateConnection(data),
    onSuccess: (data) => {
      queryClient.setQueryData(slackConnectionKeys.detail(), data);
      queryClient.invalidateQueries({ queryKey: slackConnectionKeys.all });
    },
  });
};

/**
 * Hook: useSyncSlackUsers
 * Sync Slack user IDs from workspace
 */
export const useSyncSlackUsers = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await api.post<SyncResult>('/slack/sync-users');
      return response.data;
    },
  });
};

/**
 * Hook: useTestBirthdayNotification
 * Send test birthday notification to Slack
 */
export const useTestBirthdayNotification = () => {
  return useMutation({
    mutationFn: async (data: TestBirthdayDto) => {
      await api.post('/slack/test/birthday', data);
    },
  });
};

/**
 * Hook: useTestAnniversaryNotification
 * Send test anniversary notification to Slack
 */
export const useTestAnniversaryNotification = () => {
  return useMutation({
    mutationFn: async (data: TestAnniversaryDto) => {
      await api.post('/slack/test/anniversary', data);
    },
  });
};
